import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Users, Search, Edit, Trash2, ChevronRight, RefreshCw, Database, Zap, UserCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerModal = ({ customer, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: customer?.name || "",
    contact_email: customer?.contact_email || "",
    contact_phone: customer?.contact_phone || "",
    notes: customer?.notes || "",
    isr: customer?.isr || "",
    osr: customer?.osr || "",
  });
  const [repLists, setRepLists] = useState({ isr_list: [], osr_list: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/settings/reps`)
      .then((res) => setRepLists(res.data || { isr_list: [], osr_list: [] }))
      .catch(() => setRepLists({ isr_list: [], osr_list: [] }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (customer) {
        await axios.put(`${API}/customers/${customer.id}`, form);
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, form);
        toast.success("Customer created");
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save customer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold">{customer ? "Edit Customer" : "New Customer"}</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Company Name *</label>
            <input
              data-testid="customer-name-input"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Contact Email</label>
            <input
              data-testid="customer-email-input"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Contact Phone</label>
            <input
              data-testid="customer-phone-input"
              type="text"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Default ISR Reviewer</label>
              <select
                value={form.isr}
                onChange={(e) => setForm({ ...form, isr: e.target.value })}
                className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Unassigned</option>
                {(repLists.isr_list || []).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Default OSR Reviewer</label>
              <select
                value={form.osr}
                onChange={(e) => setForm({ ...form, osr: e.target.value })}
                className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Unassigned</option>
                {(repLists.osr_list || []).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              data-testid="customer-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="customer-submit-btn"
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Saving..." : customer ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [airtableStatus, setAirtableStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const { isAdmin } = useAuth();

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`);
      setCustomers(res.data);
    } catch (err) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const checkAirtableStatus = async () => {
    try {
      const res = await axios.get(`${API}/customers/airtable-status`);
      setAirtableStatus(res.data);
    } catch (err) {
      setAirtableStatus({ connected: false });
    }
  };

  const handleAirtableSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/customers/sync-airtable`);
      toast.success(`Synced ${res.data.synced} customers (${res.data.created} new, ${res.data.updated} updated)`);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    checkAirtableStatus();
  }, []);

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success("Customer deleted");
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete customer");
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Customers</h1>
          <p className="text-muted-foreground mt-1">
            Manage customer accounts and default ISR/OSR intake routing
            {customers.length > 0 && ` • ${customers.length} total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && airtableStatus?.connected && (
            <button
              data-testid="sync-airtable-btn"
              onClick={handleAirtableSync}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing..." : "Sync Airtable"}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 px-4 py-2 mb-4 bg-emerald-950/30 border border-emerald-800 rounded-lg text-sm">
        <Zap size={16} className="text-emerald-400" />
        <span className="text-emerald-300">Customers synced from Spiro CRM</span>
        <span className="text-muted-foreground">• Use Edit to set default ISR/OSR intake reviewers</span>
      </div>

      {airtableStatus?.connected && (
        <div className="flex items-center gap-2 px-4 py-2 mb-4 bg-blue-950/30 border border-blue-800 rounded-lg text-sm">
          <Database size={16} className="text-blue-400" />
          <span className="text-blue-300">Connected to Airtable</span>
          <span className="text-muted-foreground">• Click "Sync Airtable" to pull latest customers</span>
        </div>
      )}

      <div className="relative max-w-md mb-6">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="search-customers"
          type="text"
          placeholder="Search customers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {loading ? (
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      ) : filteredCustomers.length === 0 ? (
        <div className="empty-state">
          <Users size={48} className="mb-4 opacity-50" />
          <p className="text-lg font-medium">No customers found</p>
          <p className="text-sm">Add a new customer to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.map((customer) => (
            <div
              key={customer.id}
              data-testid={`customer-card-${customer.id}`}
              className="bg-card border border-border rounded-xl p-6 card-hover"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-lg font-bold">
                  {customer.name.charAt(0)}
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button
                      data-testid={`edit-customer-${customer.id}`}
                      onClick={() => {
                        setEditCustomer(customer);
                        setShowModal(true);
                      }}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      data-testid={`delete-customer-${customer.id}`}
                      onClick={() => handleDelete(customer.id)}
                      className="p-2 hover:bg-red-950/30 text-red-400 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              <h3 className="text-lg font-semibold mb-1">
                {customer.name}
                {customer.spiro_company_id && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-emerald-950/50 text-emerald-400 rounded-full align-middle">
                    <Zap size={10} />
                    Spiro
                  </span>
                )}
              </h3>
              {customer.code && (
                <p className="text-xs text-primary font-mono mb-1">{customer.code}</p>
              )}
              {customer.address && (
                <p className="text-sm text-muted-foreground mb-1">{customer.address}</p>
              )}
              {customer.contact_email && (
                <p className="text-sm text-muted-foreground mb-3">{customer.contact_email}</p>
              )}

              <div className="bg-secondary/40 border border-border rounded-lg p-3 mb-4 text-sm">
                <div className="flex items-center gap-2 mb-1 text-muted-foreground">
                  <UserCheck size={14} />
                  <span>Default Intake Reviewers</span>
                </div>
                <p>ISR: <span className="font-medium">{customer.isr || "Unassigned"}</span></p>
                <p>OSR: <span className="font-medium">{customer.osr || "Unassigned"}</span></p>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <span className="text-sm text-muted-foreground">{customer.project_count} projects</span>
                <Link
                  to={`/projects?customer=${customer.id}`}
                  className="text-sm font-medium hover:underline flex items-center gap-1"
                >
                  View <ChevronRight size={14} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <CustomerModal
          customer={editCustomer}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            fetchCustomers();
          }}
        />
      )}
    </div>
  );
};
